import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Package,
  Loader2,
  Eye,
  MoreHorizontal,
  Building2,
  Calendar,
  Users,
} from 'lucide-react';
import { usePermission } from '@/components/guards/permission-guard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSessionState } from '@/hooks/use-session-state';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  PARTIALLY_SOLD: 'bg-yellow-100 text-yellow-700',
  FULLY_SOLD: 'bg-green-100 text-green-700',
  SETTLED: 'bg-emerald-100 text-emerald-700',
  RETURNED: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

export function ConsignmentListPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canCreate = usePermission('CONSIGNMENT', 'CREATE');

  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:consignment:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [statusFilter, setStatusFilter] = useSessionState<string>(
    'trading:consignment:statusFilter',
    'all'
  );
  const [vendorFilter, setVendorFilter] = useSessionState<string>(
    'trading:consignment:vendorFilter',
    'all'
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const { data: summaryData } = trpc.tradingConsignment.getSummary.useQuery();
  const { data: vendorsData } = trpc.tradingConsignment.listVendors.useQuery();

  const { data: consignmentsData, isLoading } = trpc.tradingConsignment.list.useQuery({
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? (statusFilter as any) : undefined,
    vendorId: vendorFilter !== 'all' ? vendorFilter : undefined,
  });

  const consignments = consignmentsData || [];
  const vendors = vendorsData || [];

  const getStatusLabel = (status: string) => {
    const key = status.toLowerCase().replace(/_/g, '');
    const statusMap: Record<string, string> = {
      draft: 'status.draft',
      sent: 'status.sent',
      partiallysold: 'status.partiallySold',
      fullysold: 'status.fullySold',
      settled: 'status.settled',
      returned: 'status.returned',
      cancelled: 'status.cancelled',
    };
    return t(`consignment.${statusMap[key] || 'status.draft'}`);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('consignment.list.title')}</h1>
          <p className="text-muted-foreground">{t('consignment.list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/trading/consignment/vendors')}>
            <Users className="mr-2 h-4 w-4" />
            {t('consignment.vendors.title')}
          </Button>
          {canCreate && (
            <Button onClick={() => navigate('/trading/consignment/new')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('consignment.list.addConsignment')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="border-border bg-card border p-4">
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium">
                {t('consignment.dashboard.activeConsignments')}
              </span>
              <Package className="text-muted-foreground h-4 w-4" />
            </div>
            <div className="text-2xl font-bold">{summaryData.activeConsignments}</div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium">{t('consignment.dashboard.pendingSales')}</span>
              <Calendar className="text-muted-foreground h-4 w-4" />
            </div>
            <div className="text-2xl font-bold">{summaryData.pendingSales}</div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium">
                {t('consignment.dashboard.pendingSettlements')}
              </span>
              <Building2 className="text-muted-foreground h-4 w-4" />
            </div>
            <div className="text-2xl font-bold">{summaryData.pendingSettlements}</div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center justify-between pb-2">
              <span className="text-sm font-medium">
                {t('consignment.dashboard.valueAtVendors')}
              </span>
              <Package className="text-muted-foreground h-4 w-4" />
            </div>
            <div className="text-2xl font-bold">
              {/* FIFO batch value at consignment warehouses — TRY book value */}
              {formatCurrency(summaryData.totalValueAtVendors, 'TRY')}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('consignment.list.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="all">{t('consignment.list.allVendors')}</option>
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.companyName}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="all">{t('consignment.list.allStatuses')}</option>
          <option value="DRAFT">{t('consignment.status.draft')}</option>
          <option value="SENT">{t('consignment.status.sent')}</option>
          <option value="PARTIALLY_SOLD">{t('consignment.status.partiallySold')}</option>
          <option value="FULLY_SOLD">{t('consignment.status.fullySold')}</option>
          <option value="SETTLED">{t('consignment.status.settled')}</option>
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{tCommon('loading')}</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && consignments.length === 0 && (
        <div className="border-border bg-card flex flex-col items-center justify-center border py-12 text-center">
          <Package className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('consignment.list.noConsignments')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('consignment.list.noConsignmentsDesc')}
          </p>
          {canCreate && (
            <Button className="mt-4" onClick={() => navigate('/trading/consignment/new')}>
              <Plus className="mr-2 h-4 w-4" />
              {t('consignment.list.addConsignment')}
            </Button>
          )}
        </div>
      )}

      {/* Consignments Table */}
      {!isLoading && consignments.length > 0 && (
        <div className="border-border bg-card border">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.consignmentNumber')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.vendor')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.warehouse')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.sentDate')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.items')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('consignment.list.status')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium">{tCommon('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {consignments.map((consignment) => (
                <tr
                  key={consignment.id}
                  className="hover:bg-muted/50 cursor-pointer border-b"
                  onClick={() => navigate(`/trading/consignment/${consignment.id}`)}
                >
                  <td className="px-4 py-2">
                    <span className="font-medium">{consignment.consignmentNumber}</span>
                  </td>
                  <td className="px-4 py-2">
                    <div>
                      <div className="font-medium">{consignment.vendor.companyName}</div>
                      {consignment.vendor.contactName && (
                        <div className="text-muted-foreground text-xs">
                          {consignment.vendor.contactName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm">{consignment.warehouse.name}</span>
                  </td>
                  <td className="px-4 py-2">
                    {consignment.sentDate ? (
                      <span className="text-sm">{formatDate(consignment.sentDate)}</span>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm">{consignment._count.items}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${statusColors[consignment.status]}`}
                    >
                      {getStatusLabel(consignment.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/trading/consignment/${consignment.id}`);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {tCommon('view')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
